import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Subject } from 'rxjs';
import { resetDocumentScrollLock } from '../utils/scroll-utils';
import GlobalLoader from './global-loader';

/** Login success UI timing (ms) — shared by Login and auth-loading-manager */
export const LOGIN_SUCCESS_TOAST_LIFE_MS = 2200;
/** After success toast: dialog closes same frame as GlobalLoader (no extra pause). */
export const LOGIN_LOADER_BEFORE_DIALOG_MS = 0;
export const LOGIN_POST_NAV_LOADER_MS = 2500;
/** Logout uses ButtonLoader in dialog first; dialog closes with 0ms delay once that phase ends. */
export const LOGOUT_LOADER_BEFORE_DIALOG_MS = 0;
/** Minimum time ButtonLoader stays visible in logout dialog before it closes. */
export const LOGOUT_BUTTON_LOADER_VISIBLE_MS = 400;
export const LOGOUT_POST_NAV_LOADER_MS = 1200;
export const SIGNUP_LOADER_BEFORE_DIALOG_MS = 100;
export const SIGNUP_POST_NAV_LOADER_MS = 1200;
/** Extra hold when category/skills submit may remount via home-shell → full-app upgrade. */
export const SIGNUP_CATEGORY_GATE_POST_NAV_LOADER_MS = 2200;
/** Covers white flash after Switch Profile dialog while lazy destination (pro-info/portfolio) mounts. */
export const PROFILE_SWITCH_POST_NAV_LOADER_MS = 1600;

const SIGNUP_OVERLAY_HOST_ID = 'virtup-signup-auth-loader';
const PROFILE_SWITCH_OVERLAY_HOST_ID = 'virtup-profile-switch-loader';

const LOGOUT_FLOW_DEBUG = false;

const logLogoutFlow = (step: string, detail?: Record<string, unknown>): void => {
  if (!LOGOUT_FLOW_DEBUG) return;
  console.log(`[LogoutFlow] ${step}`, detail ?? '');
};

class AuthLoadingManager {
  private static instance: AuthLoadingManager;
  private authLoadingSubject = new Subject<boolean>();
  private authTransitionSubject = new Subject<void>();
  private _isAuthChecking = false;
  private authCheckTimeout: NodeJS.Timeout | null = null;
  private activeSource: string | null = null;
  /** Timers for post-dialog lottie — live outside Login so unmount does not cancel them. */
  private postLoginFlowTimers: number[] = [];
  /** True while login toast → dialog close sequence is in progress (blocks early loaders). */
  private loginUiTransitionActive = false;
  /** True while logout dialog → guest home navigation is in progress. */
  private logoutUiTransitionActive = false;
  /** True while signup interest → post-auth navigation is in progress. */
  private signupUiTransitionActive = false;
  /** True while logout dialog shows ButtonLoader (block global loader). */
  private logoutButtonLoaderPhase = false;
  private appBootstrapComplete = false;
  /** Outside #root so home-shell → full-app remount does not flash white. */
  private signupOverlayRoot: Root | null = null;
  private profileSwitchOverlayRoot: Root | null = null;
  private profileSwitchOverlayStartedAt = 0;
  private profileSwitchOverlayEndTimer: number | null = null;

  private constructor() {
    this.authLoadingSubject.subscribe((isChecking) => {
      this._isAuthChecking = isChecking;
    });
  }

  public static getInstance(): AuthLoadingManager {
    if (!AuthLoadingManager.instance) {
      AuthLoadingManager.instance = new AuthLoadingManager();
    }
    return AuthLoadingManager.instance;
  }

  private clearPostLoginFlowTimers(): void {
    this.postLoginFlowTimers.forEach((timerId) => window.clearTimeout(timerId));
    this.postLoginFlowTimers = [];
  }

  private notifyAuthTransitionChanged(): void {
    this.authTransitionSubject.next();
  }

  public getAuthTransitionObservable() {
    return this.authTransitionSubject.asObservable();
  }

  public isAuthTransitionOverlayMode(): boolean {
    return (
      this.loginUiTransitionActive ||
      this.logoutUiTransitionActive ||
      this.signupUiTransitionActive
    );
  }

  public beginLogoutButtonLoaderPhase(): void {
    logLogoutFlow('beginLogoutButtonLoaderPhase');
    this.logoutButtonLoaderPhase = true;
    this.notifyAuthTransitionChanged();
  }

  public endLogoutButtonLoaderPhase(): void {
    logLogoutFlow('endLogoutButtonLoaderPhase');
    this.logoutButtonLoaderPhase = false;
    this.notifyAuthTransitionChanged();
  }

  public isLogoutButtonLoaderPhase(): boolean {
    return this.logoutButtonLoaderPhase;
  }

  public markAppBootstrapComplete(): void {
    this.appBootstrapComplete = true;
  }

  public isAppBootstrapComplete(): boolean {
    return this.appBootstrapComplete;
  }

  /** Call when login success toast starts — suppresses stray loaders until login-flow lottie. */
  public beginLoginUiTransition(): void {
    this.loginUiTransitionActive = true;
    this.notifyAuthTransitionChanged();
    this.stopAuthCheck('login-transition-reset', { force: true });
  }

  public endLoginUiTransition(): void {
    this.loginUiTransitionActive = false;
    this.notifyAuthTransitionChanged();
    this.clearPostLoginFlowTimers();
  }

  public isLoginUiTransitionActive(): boolean {
    return this.loginUiTransitionActive;
  }

  public beginLogoutUiTransition(): void {
    logLogoutFlow('beginLogoutUiTransition');
    this.logoutUiTransitionActive = true;
    this.notifyAuthTransitionChanged();
  }

  public endLogoutUiTransition(): void {
    logLogoutFlow('endLogoutUiTransition');
    this.logoutUiTransitionActive = false;
    this.notifyAuthTransitionChanged();
    this.clearPostLoginFlowTimers();
  }

  public isLogoutUiTransitionActive(): boolean {
    return this.logoutUiTransitionActive;
  }

  public beginSignupUiTransition(): void {
    this.signupUiTransitionActive = true;
    this.notifyAuthTransitionChanged();
    this.stopAuthCheck('signup-transition-reset', { force: true });
  }

  public endSignupUiTransition(): void {
    this.signupUiTransitionActive = false;
    this.notifyAuthTransitionChanged();
    this.clearPostLoginFlowTimers();
  }

  public isSignupUiTransitionActive(): boolean {
    return this.signupUiTransitionActive;
  }

  private mountSignupOverlay(): void {
    if (typeof document === 'undefined') {
      return;
    }
    let host = document.getElementById(SIGNUP_OVERLAY_HOST_ID);
    if (!host) {
      host = document.createElement('div');
      host.id = SIGNUP_OVERLAY_HOST_ID;
      document.body.appendChild(host);
    }
    if (!this.signupOverlayRoot) {
      this.signupOverlayRoot = createRoot(host);
    }
    this.signupOverlayRoot.render(
      React.createElement(GlobalLoader, { isVisible: true, type: 'auth' })
    );
  }

  private unmountSignupOverlay(): void {
    if (!this.signupOverlayRoot) {
      return;
    }
    this.signupOverlayRoot.render(
      React.createElement(GlobalLoader, { isVisible: false, type: 'auth' })
    );
    const root = this.signupOverlayRoot;
    this.signupOverlayRoot = null;
    window.setTimeout(() => {
      try {
        root.unmount();
      } catch {
        /* already unmounted */
      }
      document.getElementById(SIGNUP_OVERLAY_HOST_ID)?.remove();
    }, 350);
  }

  /**
   * Body-level overlay so Switch Profile → pro-info/portfolio does not flash white when
   * the dialog unmounts with the home page (esp. home-shell).
   */
  public beginProfileSwitchNavOverlay(): void {
    if (typeof document === 'undefined') {
      return;
    }
    if (this.profileSwitchOverlayEndTimer !== null) {
      window.clearTimeout(this.profileSwitchOverlayEndTimer);
      this.profileSwitchOverlayEndTimer = null;
    }
    this.profileSwitchOverlayStartedAt = Date.now();
    let host = document.getElementById(PROFILE_SWITCH_OVERLAY_HOST_ID);
    if (!host) {
      host = document.createElement('div');
      host.id = PROFILE_SWITCH_OVERLAY_HOST_ID;
      document.body.appendChild(host);
    }
    if (!this.profileSwitchOverlayRoot) {
      this.profileSwitchOverlayRoot = createRoot(host);
    }
    this.profileSwitchOverlayRoot.render(
      React.createElement(GlobalLoader, { isVisible: true, type: 'auth' })
    );
  }

  public endProfileSwitchNavOverlay(
    minVisibleMs = PROFILE_SWITCH_POST_NAV_LOADER_MS
  ): void {
    if (!this.profileSwitchOverlayRoot) {
      return;
    }

    if (this.profileSwitchOverlayEndTimer !== null) {
      return;
    }

    const elapsed = Date.now() - this.profileSwitchOverlayStartedAt;
    const remaining =
      minVisibleMs <= 0 ? 0 : Math.max(minVisibleMs - elapsed, 400);

    this.profileSwitchOverlayEndTimer = window.setTimeout(() => {
      this.profileSwitchOverlayEndTimer = null;
      if (!this.profileSwitchOverlayRoot) {
        return;
      }
      this.profileSwitchOverlayRoot.render(
        React.createElement(GlobalLoader, { isVisible: false, type: 'auth' })
      );
      const root = this.profileSwitchOverlayRoot;
      this.profileSwitchOverlayRoot = null;
      window.setTimeout(() => {
        try {
          root.unmount();
        } catch {
          /* already unmounted */
        }
        document.getElementById(PROFILE_SWITCH_OVERLAY_HOST_ID)?.remove();
      }, 350);
    }, remaining);
  }

  /**
   * Button loader in dialog → close dialog → global loader ON → navigate + sync session → loader OFF.
   */
  public runSignupFlow(
    onDialogClose?: () => void,
    performSignup?: () => void | Promise<void>,
    loaderBeforeDialogMs = SIGNUP_LOADER_BEFORE_DIALOG_MS,
    loaderMs = SIGNUP_POST_NAV_LOADER_MS
  ): void {
    this.clearPostLoginFlowTimers();
    this.beginSignupUiTransition();
    this.mountSignupOverlay();

    const dialogTimer = window.setTimeout(() => {
      onDialogClose?.();
      requestAnimationFrame(() => {
        this.startAuthCheck('signup-flow');
        const loaderStartedAt = Date.now();

        void (async () => {
          try {
            await performSignup?.();
          } finally {
            const elapsed = Date.now() - loaderStartedAt;
            const remaining = Math.max(loaderMs - elapsed, 500);
            const stopTimer = window.setTimeout(() => {
              this.stopAuthCheck('signup-flow');
              this.endSignupUiTransition();
              this.unmountSignupOverlay();
            }, remaining);
            this.postLoginFlowTimers.push(stopTimer);
          }
        })();
      });
    }, loaderBeforeDialogMs);
    this.postLoginFlowTimers.push(dialogTimer);
  }

  /**
   * Caller shows ButtonLoader in dialog first → close dialog → global loader ON → logout work → loader OFF.
   */
  public runLogoutFlow(
    onDialogClose?: () => void,
    performLogout?: () => void | Promise<void>,
    loaderBeforeDialogMs = LOGOUT_LOADER_BEFORE_DIALOG_MS,
    loaderMs = LOGOUT_POST_NAV_LOADER_MS,
    onAfterLoader?: () => void
  ): void {
    logLogoutFlow('runLogoutFlow:start', { loaderBeforeDialogMs, loaderMs });
    this.clearPostLoginFlowTimers();

    const startGlobalLoaderAndLogout = (): void => {
      logLogoutFlow('globalLoader:on');
      this.beginLogoutUiTransition();
      this.startAuthCheck('logout-flow');
      this.endLogoutButtonLoaderPhase();

      logLogoutFlow('dialog:close');
      onDialogClose?.();

      const loaderStartedAt = Date.now();

      void (async () => {
        try {
          logLogoutFlow('performLogout:start');
          await performLogout?.();
          logLogoutFlow('performLogout:done');
        } finally {
          const elapsed = Date.now() - loaderStartedAt;
          const remaining = Math.max(loaderMs - elapsed, 500);
          logLogoutFlow('globalLoader:stopScheduled', { remaining });
          const stopTimer = window.setTimeout(() => {
            this.stopAuthCheck('logout-flow');
            this.endLogoutUiTransition();
            resetDocumentScrollLock();
            onAfterLoader?.();
            logLogoutFlow('globalLoader:off');
          }, remaining);
          this.postLoginFlowTimers.push(stopTimer);
        }
      })();
    };

    if (loaderBeforeDialogMs > 0) {
      const dialogTimer = window.setTimeout(
        startGlobalLoaderAndLogout,
        loaderBeforeDialogMs
      );
      this.postLoginFlowTimers.push(dialogTimer);
    } else {
      startGlobalLoaderAndLogout();
    }
  }

  /**
   * Toast ends → GlobalLoader ON + dialog closes instantly → navigate → loader OFF.
   * Timers live here so Login unmount does not cancel them.
   */
  public runPostDialogLoginFlow(
    onDialogClose?: () => void,
    afterDialogClose?: () => void,
    loaderBeforeDialogMs = LOGIN_LOADER_BEFORE_DIALOG_MS,
    loaderMs = LOGIN_POST_NAV_LOADER_MS
  ): void {
    this.clearPostLoginFlowTimers();

    const startLoaderPhase = (): void => {
      this.startAuthCheck('login-flow');
      const loaderStartedAt = Date.now();

      onDialogClose?.();
      afterDialogClose?.();

      const elapsed = Date.now() - loaderStartedAt;
      const remaining = Math.max(loaderMs - elapsed, 500);

      const stopTimer = window.setTimeout(() => {
        this.stopAuthCheck('login-flow');
        this.endLoginUiTransition();
      }, remaining);
      this.postLoginFlowTimers.push(stopTimer);
    };

    if (loaderBeforeDialogMs > 0) {
      const dialogTimer = window.setTimeout(startLoaderPhase, loaderBeforeDialogMs);
      this.postLoginFlowTimers.push(dialogTimer);
    } else {
      startLoaderPhase();
    }
  }

  public startAuthCheck(source = 'unknown'): void {
    if (this.logoutButtonLoaderPhase && source !== 'logout-flow') {
      logLogoutFlow('startAuthCheck:blocked-during-button-loader', { source });
      return;
    }
    if (this.loginUiTransitionActive && source !== 'login-flow') {
      return;
    }
    if (this.logoutUiTransitionActive && source !== 'logout-flow') {
      return;
    }
    if (this.signupUiTransitionActive && source !== 'signup-flow') {
      return;
    }
    if (this.activeSource === 'login-flow' && source !== 'login-flow') {
      return;
    }
    if (this.activeSource === 'logout-flow' && source !== 'logout-flow') {
      return;
    }
    if (this.activeSource === 'signup-flow' && source !== 'signup-flow') {
      return;
    }
    if (this.authCheckTimeout) {
      clearTimeout(this.authCheckTimeout);
    }

    this.activeSource = source;
    this.authLoadingSubject.next(true);
    if (source === 'logout-flow') {
      logLogoutFlow('startAuthCheck', { source, activeSource: this.activeSource });
    }

    this.authCheckTimeout = setTimeout(() => {
      this.stopAuthCheck('auto-timeout', { force: true });
    }, 60000);
  }

  public stopAuthCheck(source = 'unknown', options?: { force?: boolean }): void {
    const force = options?.force === true;
    if (
      !force &&
      this.activeSource === 'login-flow' &&
      source !== 'login-flow' &&
      source !== 'auto-timeout'
    ) {
      return;
    }
    if (
      !force &&
      this.activeSource === 'logout-flow' &&
      source !== 'logout-flow' &&
      source !== 'auto-timeout'
    ) {
      return;
    }
    if (
      !force &&
      this.activeSource === 'signup-flow' &&
      source !== 'signup-flow' &&
      source !== 'auto-timeout'
    ) {
      return;
    }
    if (
      !force &&
      !this._isAuthChecking &&
      source !== 'login-transition-reset' &&
      source !== 'logout-transition-reset' &&
      source !== 'signup-transition-reset'
    ) {
      return;
    }

    if (this.authCheckTimeout) {
      clearTimeout(this.authCheckTimeout);
      this.authCheckTimeout = null;
    }
    if (this.activeSource === source || force || source === 'auto-timeout') {
      this.activeSource = null;
    }
    this.authLoadingSubject.next(false);
  }

  public isAuthChecking(): boolean {
    return this._isAuthChecking;
  }

  public getAuthLoadingObservable() {
    return this.authLoadingSubject.asObservable();
  }
}

export const authLoadingManager = AuthLoadingManager.getInstance();
